import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Badge } from '@components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Textarea } from '@components/ui/textarea';
import { Label } from '@components/ui/label';
import {
  getRegistrations,
  updateRegistrationStatus,
  getStatusCounts,
} from '@generated/server/registrations';
import { assignCoupons } from '@generated/server/coupons';
import { Search, Loader2, CheckCircle, XCircle, Clock, Send, Copy, Check } from 'lucide-react';

export const Route = createFileRoute('/_app/management')({ component: ManagementDashboard });

type Registration = Awaited<ReturnType<typeof getRegistrations>>['items'][number];
type StatusCount = Awaited<ReturnType<typeof getStatusCounts>>[number];

function ManagementDashboard() {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | 'sms' | null>(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [copiedPhones, setCopiedPhones] = useState(false);

  const loadRegistrations = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getRegistrations({
        data: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: search || undefined,
          cursor: isLoadMore ? cursor || undefined : undefined,
        },
      });

      if (isLoadMore) {
        setRegistrations((prev) => [...prev, ...(result?.items || [])]);
      } else {
        setRegistrations(result?.items || []);
      }
      setCursor(result?.cursor || null);
    } catch (err) {
      console.error('Failed to load registrations:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadStats = async () => {
    try {
      const counts = await getStatusCounts();
      setStatusCounts(counts || []);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  useEffect(() => {
    loadRegistrations();
    loadStats();
  }, [statusFilter]);

  const handleAction = async () => {
    if (!selectedRegistration || !action) return;
    setProcessing(true);

    try {
      if (action === 'approve') {
        const allIds = [selectedRegistration.column_9 || ''];
        if (selectedRegistration.column__) {
          const additionalIds = (selectedRegistration.column__ as string)
            .split('\n')
            .map((line: string) => line.split(':')[1]?.trim())
            .filter(Boolean);
          allIds.push(...additionalIds);
        }

        await assignCoupons({
          data: {
            registrationId: selectedRegistration.id,
            idNumbers: allIds,
          },
        });

        await updateRegistrationStatus({
          data: {
            itemId: selectedRegistration.id,
            status: 'אושר',
            notes,
          },
        });
      } else if (action === 'reject') {
        await updateRegistrationStatus({
          data: {
            itemId: selectedRegistration.id,
            status: 'נדחה',
            notes,
          },
        });
      } else if (action === 'sms') {
        await updateRegistrationStatus({
          data: {
            itemId: selectedRegistration.id,
            status: 'נשלח SMS',
            notes,
          },
        });
      }

      setActionDialog(false);
      setNotes('');
      setSelectedRegistration(null);
      setAction(null);
      loadRegistrations();
      loadStats();
    } catch (err) {
      console.error('Failed to process action:', err);
      alert('אירעה שגיאה בעיבוד הבקשה');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;

    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof Clock }> = {
      'ממתין לבדיקה': { variant: 'secondary', icon: Clock },
      אושר: { variant: 'default', icon: CheckCircle },
      נדחה: { variant: 'destructive', icon: XCircle },
      'נשלח SMS': { variant: 'outline', icon: Send },
    };

    const config = variants[status] || { variant: 'secondary' as const, icon: Clock };
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const copyApprovedPhones = () => {
    const approvedRegs = registrations.filter((r) => r.column_6 === 'אושר');
    const phones = approvedRegs.map((r) => r.column_10?.phone).filter(Boolean);
    const phoneText = phones.join('\n');

    navigator.clipboard.writeText(phoneText);
    setCopiedPhones(true);
    setTimeout(() => setCopiedPhones(false), 2000);
  };

  const getStatCount = (status: string) => {
    const stat = statusCounts.find((s) => s.column_6 === status);
    return stat?.count || 0;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" dir="rtl">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-[hsl(var(--foreground))]">ניהול רישומים</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            בדיקה ואישור רישומים לכרטיסים מסובסדים
          </p>
        </div>

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">ממתין לבדיקה</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('ממתין לבדיקה')}
            </p>
          </div>

          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[hsl(var(--chart-1))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">אושר</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('אושר')}
            </p>
          </div>

          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-[hsl(var(--accent))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">נשלח SMS</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('נשלח SMS')}
            </p>
          </div>

          <div className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-[hsl(var(--destructive))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">נדחה</span>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-[hsl(var(--foreground))]">
              {getStatCount('נדחה')}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              placeholder="חיפוש לפי ת.ז..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadRegistrations();
              }}
              className="pr-10 rounded-full"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 rounded-full">
              <SelectValue placeholder="כל הסטטוסים" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">כל הסטטוסים</SelectItem>
              <SelectItem value="ממתין לבדיקה">ממתין לבדיקה</SelectItem>
              <SelectItem value="אושר">אושר</SelectItem>
              <SelectItem value="נדחה">נדחה</SelectItem>
              <SelectItem value="נשלח SMS">נשלח SMS</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={() => loadRegistrations()} variant="outline" className="rounded-full">
            חיפוש
          </Button>

          <Button
            onClick={copyApprovedPhones}
            variant="secondary"
            className="rounded-full gap-2"
            disabled={registrations.filter((r) => r.column_6 === 'אושר').length === 0}
          >
            {copiedPhones ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            העתק מספרים
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
          </div>
        ) : (
          <div className="space-y-3">
            {registrations.map((reg) => (
              <div
                key={reg.id}
                className="rounded-[12px] bg-[hsl(var(--popover))] p-4 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] transition-colors"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-[hsl(var(--foreground))]">{reg.name}</h3>
                      {getStatusBadge(reg.column_6)}
                    </div>

                    <div className="grid gap-2 text-sm sm:grid-cols-2">
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">ת.ז: </span>
                        <span className="text-[hsl(var(--foreground))]">{reg.column_9}</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">טלפון: </span>
                        <span className="text-[hsl(var(--foreground))]" dir="ltr">
                          {reg.column_10?.phone}
                        </span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">כתובת: </span>
                        <span className="text-[hsl(var(--foreground))]">
                          {reg.column} {reg.column_2}
                        </span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">כרטיסים: </span>
                        <span className="text-[hsl(var(--foreground))]">{reg.column_4 ?? 1}</span>
                      </div>
                    </div>

                    {reg.column__ && (
                      <div className="pt-2 border-t border-[hsl(var(--border))]">
                        <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">
                          נרשמים נוספים:
                        </p>
                        <p className="text-sm text-[hsl(var(--foreground))] whitespace-pre-line">
                          {reg.column__}
                        </p>
                      </div>
                    )}

                    {reg.column_7 && (
                      <div className="pt-2 border-t border-[hsl(var(--border))]">
                        <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1">
                          הערות מנהל:
                        </p>
                        <p className="text-sm text-[hsl(var(--foreground))]">{reg.column_7}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 sm:flex-col">
                    {reg.column_6 === 'ממתין לבדיקה' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedRegistration(reg);
                            setAction('approve');
                            setActionDialog(true);
                          }}
                          className="rounded-full flex-1 sm:flex-initial"
                        >
                          <CheckCircle className="h-4 w-4 sm:ml-2" />
                          <span className="hidden sm:inline">אשר</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedRegistration(reg);
                            setAction('reject');
                            setActionDialog(true);
                          }}
                          className="rounded-full flex-1 sm:flex-initial"
                        >
                          <XCircle className="h-4 w-4 sm:ml-2" />
                          <span className="hidden sm:inline">דחה</span>
                        </Button>
                      </>
                    )}

                    {reg.column_6 === 'אושר' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedRegistration(reg);
                          setAction('sms');
                          setActionDialog(true);
                        }}
                        className="rounded-full"
                      >
                        <Send className="h-4 w-4 sm:ml-2" />
                        <span className="hidden sm:inline">סמן נשלח</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {registrations.length === 0 && (
              <div className="py-12 text-center">
                <p className="text-[hsl(var(--muted-foreground))]">לא נמצאו רישומים</p>
              </div>
            )}

            {cursor && (
              <div className="flex justify-center pt-4">
                <Button onClick={() => loadRegistrations(true)} disabled={loadingMore} className="rounded-full">
                  {loadingMore ? (
                    <>
                      <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                      טוען...
                    </>
                  ) : (
                    'טען עוד'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={actionDialog} onOpenChange={setActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' && 'אישור רישום'}
              {action === 'reject' && 'דחיית רישום'}
              {action === 'sms' && 'סימון כנשלח SMS'}
            </DialogTitle>
            <DialogDescription>
              {action === 'approve' && `רישום עבור ${selectedRegistration?.name} יאושר ויוקצה קופון`}
              {action === 'reject' && `רישום עבור ${selectedRegistration?.name} יידחה`}
              {action === 'sms' && `רישום עבור ${selectedRegistration?.name} יסומן כ"נשלח SMS"`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notes">הערות (אופציונלי)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="הוסף הערות..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(false)} disabled={processing}>
              ביטול
            </Button>
            <Button onClick={handleAction} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  מעבד...
                </>
              ) : (
                'אישור'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
